import {
  ArrowRightIcon,
  Box,
  Button,
  ExternalLinkIcon,
  InfoIcon,
  Link,
  RefreshIcon,
  SearchInput,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TablePagination,
  TableRow,
  TableToolbar,
  TableToolbarActions,
  Text,
  Tooltip,
  TooltipInteractiveWrapper,
} from '@razorpay/blade/components';
import api from 'api';
import { getErrorsAsString } from 'components/Kyc/utils';
import { routePaths } from 'components/Routes/data';
import { StatusBadge } from 'components/SalaryComponents/components/StatusBadge';
import { TextWithDescription } from 'components/SalaryComponents/components/TextWithDescription';
import {
  earningFiltersMap,
  helpArticles,
  salaryComponentParams,
  salaryComponentsQueries,
  statusFilters,
} from 'components/SalaryComponents/constants';
import { EarningsComponentType } from 'components/SalaryComponents/types';
import {
  getQueryVariables,
  getTableItemCountLimits,
  getTaxRegimeText,
  sortSalaryComponents,
} from 'components/SalaryComponents/utils';
import { earningPayTypes } from 'components/WizardViews/SalaryComponents/constants';
import { earningsExemptionDisplayText } from 'components/WizardViews/SalaryComponents/utils';
import { EmptyListView } from 'components/ui/EmptyListView/EmptyListView';
import { ListViewLoader } from 'components/ui/ListViewLoader';
import { useFilters } from 'hooks/useFilters';
import { usePagination } from 'hooks/usePagination';
import React, { useEffect, useMemo, useState } from 'react';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import { AppError } from 'utils/AppError';
import { useDetailsView } from '../../hooks/useDetailsView';

const EarningsListView = ({
  onCountChange,
}: {
  onCountChange: (counts: Record<keyof typeof statusFilters, number>) => void;
}) => {
  const history = useHistory();
  const { currentPage, itemsPerPage, setCurrentPage, setItemsPerPage } = usePagination({
    page: 0,
    limit: 10,
  });

  const [searchFilter, setSearchFilter] = useState('');
  const { debouncedValue: debouncedSearch } = useDebouncedValue(searchFilter, 600);

  const { currentFilters, isAnyFilterApplied } = useFilters({
    filtersMap: earningFiltersMap,
    multipleValueFilters: [earningFiltersMap.taxability],
    onParamsChange(params) {
      setCurrentPage(0);
    },
  });

  const queryVariables = getQueryVariables('earnings', currentFilters, debouncedSearch);

  const { data, isFetching, refetch, error, isError } = useQuery({
    queryKey: [salaryComponentsQueries.getSalaryComponentsList, queryVariables],
    queryFn: () =>
      api.salaryComponents.queries.listSalaryComponents<EarningsComponentType>(queryVariables),
    select(data) {
      // Filter out non-payable benefits from earnings
      return data.payrollListSalaryComponents.data.salaryComponents.filter(
        (component: any) => component.settings.earningDetails?.payType !== 'non_payable_benefit'
      );
    }
  });

  const nodes = useMemo(() => sortSalaryComponents(data ?? []), [data]);

  // Use useEffect to calculate and set counts only when needed
  useEffect(() => {
    if (onCountChange && nodes.length > 0) {
      onCountChange({
        all: nodes.length,
        active: nodes.filter((node) => node.isActive).length,
        disabled: nodes.filter((node) => !node.isActive).length,
      });
    }
  }, [nodes, onCountChange]);

  const { openDetailsView } = useDetailsView(salaryComponentParams.componentId);

  const isTableEmpty = nodes.length === 0;

  const errorString = getErrorsAsString(error as AppError);

  return (
    <>
      <Table
        data={{ nodes }}
        pagination={
          !isTableEmpty ? (
            <TablePagination
              currentPage={currentPage}
              showPageSizePicker={true}
              onPageChange={({ page }) => {
                setCurrentPage(page);
              }}
              onPageSizeChange={({ pageSize }) => {
                setItemsPerPage(pageSize);
              }}
              totalItemCount={nodes.length}
            />
          ) : undefined
        }
        toolbar={
          <TableToolbar title={getTableItemCountLimits(nodes.length, currentPage, itemsPerPage)}>
            <TableToolbarActions>
              <Box display="flex" flexDirection="row" gap="spacing.4" alignItems="center"justifyContent={'end'}>
                <Box width="250px">
                <SearchInput
                  label=""
                  placeholder="Search Component"
                  value={searchFilter}
                  onChange={({ value }) => {
                    setSearchFilter(value || '');
                    setCurrentPage(0);
                  }}
                  onClearButtonClick={() => {
                    setSearchFilter('');
                    setCurrentPage(0);
                  }}
                />
                </Box>
                <Button
                  icon={RefreshIcon}
                  variant={'tertiary'}
                  onClick={refetch}
                />
              </Box>
            </TableToolbarActions>
          </TableToolbar>
        }
        showBorderedCells={false}>
        {(salaryComponents) => (
          <>
            <TableHeader>
              <TableHeaderRow>
                <TableHeaderCell>
                  <Text weight="semibold">Component Name</Text>
                </TableHeaderCell>
                <TableHeaderCell>
                  <Box display={'flex'} flexDirection={'row'} gap={'spacing.3'}>
                    <Text weight="semibold">Display Name</Text>
                    <Tooltip
                      placement="top"
                      content={`The name that will appear in employee's payslip and Salary Register`}>
                      <TooltipInteractiveWrapper>
                        <InfoIcon marginTop={'spacing.1'} />
                      </TooltipInteractiveWrapper>
                    </Tooltip>
                  </Box>
                </TableHeaderCell>
                <TableHeaderCell>
                  <Text weight="semibold">Pay Type</Text>
                </TableHeaderCell>
                <TableHeaderCell>
                  <Text weight="semibold">Taxability</Text>
                </TableHeaderCell>
                <TableHeaderCell>
                  <Text weight="semibold" marginX={'auto'}>
                    Status
                  </Text>
                </TableHeaderCell>
              </TableHeaderRow>
            </TableHeader>
            {isFetching ? (
              <ListViewLoader columnCount={5} accessibilityLabel="loading earnings components" />
            ) : (
              <TableBody>
                {salaryComponents.map((salaryComponent) => {
                  const exemptionText = earningsExemptionDisplayText(
                    salaryComponent.settings.behaviour.taxBehaviour?.taxExemptionSection,
                    salaryComponent.settings.earningDetails.payType,
                  );
                  return (
                    <TableRow
                      key={salaryComponent.id}
                      item={salaryComponent}
                      onClick={() =>
                        openDetailsView(
                          salaryComponent.id,
                          salaryComponent.settings.earningDetails.payType,
                        )
                      }
                      hoverActions={
                        <Button
                          variant="tertiary"
                          color="primary"
                          size="medium"
                          icon={ArrowRightIcon}
                          iconPosition="right"
                          onClick={() =>
                            openDetailsView(
                              salaryComponent.id,
                              salaryComponent.settings.earningDetails.payType,
                            )
                          }
                          testID={`view-details-button-${salaryComponent.id}`}>
                          View Details
                        </Button>
                      }>
                      <TableCell>
                        <TextWithDescription
                          componentName={salaryComponent.name}
                          description={salaryComponent.isPredefined ? 'Predefined' : 'Custom'}
                        />
                      </TableCell>
                      <TableCell>
                        {salaryComponent.settings?.behaviour.displayBehaviour?.displayName ?? ''}
                      </TableCell>
                      <TableCell>
                        {salaryComponent.settings?.earningDetails?.payType
                          ? earningPayTypes[
                              salaryComponent.settings?.earningDetails
                                ?.payType as keyof typeof earningPayTypes
                            ].label
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <TextWithDescription
                          componentName={`${
                            Boolean(
                              salaryComponent.settings.behaviour.taxBehaviour
                                ?.taxExemptInOldRegime ||
                                salaryComponent.settings.behaviour.taxBehaviour
                                  ?.taxExemptInNewRegime,
                            )
                              ? 'Exempted'
                              : 'Taxable'
                          }${
                            salaryComponent.settings.behaviour.taxBehaviour?.taxExemptionSection
                              ? ` | ${exemptionText}`
                              : ''
                          }`}
                          description={getTaxRegimeText(
                            salaryComponent.settings.behaviour.taxBehaviour?.taxExemptInOldRegime,
                            salaryComponent.settings.behaviour.taxBehaviour?.taxExemptInNewRegime,
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <Box marginX={'auto'}>
                          <StatusBadge isActive={salaryComponent.isActive} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            )}
          </>
        )}
      </Table>
      {isTableEmpty && !isFetching && !isError && (
        <EmptyListView
          title={
            isAnyFilterApplied ? 'No Search Results Found' : ' No Earnings component created yet 🙈'
          }
          description={
            <Text color={'surface.text.gray.muted'} weight={'medium'} textAlign={'center'}>
              Click on Create Component button to create your first component
            </Text>
          }>
          <Box display={'flex'} flexDirection={'column'} gap={'spacing.4'} marginX={'auto'}>
            <Link
              size="medium"
              variant="anchor"
              href={helpArticles.learnMore} // link to be added
              icon={ExternalLinkIcon}
              iconPosition="right"
              target="_blank">
              Learn more about salary component
            </Link>
            <Box marginX={'auto'}>
              <Button
                size="medium"
                onClick={() => {
                  history.push(routePaths.salaryComponents.create.earnings.root);
                }}
                isFullWidth>
                Create Earnings Component
              </Button>
            </Box>
          </Box>
        </EmptyListView>
      )}
      {isError && (
        <EmptyListView
          title={`There was an error while fetching the data for ${
            currentFilters.status ?? 'all'
          } earning components. `}
          description={errorString ? `Error: ${errorString}` : null}
          isError>
          <Box display={'flex'} flexDirection={'column'} gap={'spacing.4'} marginX={'auto'}>
            <Link
              size="medium"
              variant="anchor"
              href={helpArticles.learnMore} // link to be added
              icon={ExternalLinkIcon}
              iconPosition="right"
              target="_blank">
              Learn more about salary component
            </Link>
            <Box marginX={'auto'}>
              <Button
                size="medium"
                onClick={() => {
                  refetch();
                }}
                isFullWidth
                iconPosition="left"
                icon={RefreshIcon}>
                Retry
              </Button>
            </Box>
          </Box>
        </EmptyListView>
      )}
    </>
  );
};

export default EarningsListView;
